import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, type OptionalString } from './hooks/useStepDetailRender';
import EditToolbar, { useEditToolbar } from '../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../graphql/queries/getTmsTaskDetail';
import {
    HANDLE_TMS_WHS_SEALED_SCAN_EVENT_FOR_PORTAL,
    type HandleTmsWhsSealedScanEventInput,
} from '../../graphql/mutations/taskMutations';

interface SealingDetailProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    task?: TmsTaskDetailType;
}

const SealingDetail: React.FC<SealingDetailProps> = ({ waypoint, stepDetailAllowEdit, task }) => {
    const formatTimeDisplay = (value: OptionalString) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : null);

    const [sealTime, setSealTime] = useState<OptionalString>(formatTimeDisplay(waypoint.sealTime));
    const [sealTimeTimezone] = useState<OptionalString>(waypoint.sealTimeTimezone);
    const [sealNumber, setSealNumber] = useState<string>(waypoint.sealNumber || '');

    const [sealTimeDraft, setSealTimeDraft] = useState<Dayjs | null>(
        waypoint.sealTime ? dayjs(waypoint.sealTime) : null
    );
    const [sealNumberDraft, setSealNumberDraft] = useState<string>(waypoint.sealNumber || '');

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setSealTimeDraft(waypoint.sealTime ? dayjs(waypoint.sealTime) : null);
            setSealNumberDraft(waypoint.sealNumber || '');
        }, [waypoint.sealTime, waypoint.sealNumber]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [handleTmsWhsSealedScanEventMutation] = useMutation<
        { handleTmsWhsSealedScanEventForPortal: { success: boolean; message: string } },
        { input: HandleTmsWhsSealedScanEventInput }
    >(HANDLE_TMS_WHS_SEALED_SCAN_EVENT_FOR_PORTAL);

    const sealTimeLabel = t('tms.task.drawer.sealTime');
    const sealNumberLabel = t('tms.task.drawer.sealNumber');

    const { renderTimeRow, renderInputRow } = useStepDetailRender({ isEditing });

    useEffect(() => {
        setSealTime(formatTimeDisplay(waypoint.sealTime));
        setSealTimeDraft(waypoint.sealTime ? dayjs(waypoint.sealTime) : null);
    }, [waypoint.sealTime]);

    useEffect(() => {
        setSealNumber(waypoint.sealNumber || '');
        setSealNumberDraft(waypoint.sealNumber || '');
    }, [waypoint.sealNumber]);

    const validateSave = () => {
        if (!sealTimeDraft) {
            message.error(t('tms.task.drawer.sealTimeRequired'));
            return false;
        }
        const trimmed = sealNumberDraft.trim();
        if (!trimmed) {
            message.error(t('tms.task.drawer.sealNumberRequired'));
            return false;
        }
        if (trimmed.length > 32) {
            message.error(t('tms.task.drawer.sealNumberTooLong'));
            return false;
        }
        return true;
    };

    const handleSave = useCallback(async () => {
        if (!validateSave()) return;

        const sealLocalTime = sealTimeDraft ? sealTimeDraft.format('YYYY-MM-DD HH:mm') : null;
        const variables = {
            input: {
                taskId: waypoint.taskId,
                taskWayPointId: Number(waypoint.id),
                sealNumber: sealNumberDraft.trim(),
                forceSubmit: true,
                sealLocalTime,
            },
        };

        try {
            const { data } = await handleTmsWhsSealedScanEventMutation({
                variables,
                refetchQueries: [{ query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } }],
                awaitRefetchQueries: true,
            });
            if (!data?.handleTmsWhsSealedScanEventForPortal.success) {
                throw new Error(data?.handleTmsWhsSealedScanEventForPortal.message || t('tms.task.drawer.sealNumberSaveFailed'));
            }
            setSealTime(formatTimeDisplay(sealLocalTime));
            setSealNumber(sealNumberDraft.trim());
            message.success(t('tms.task.drawer.sealNumberSaved'));
            exitEdit();
        } catch (error: any) {
            console.error('Failed to save seal:', error);
            message.error(error?.message || t('tms.task.drawer.sealNumberSaveFailed'));
        }
    }, [waypoint.taskId, waypoint.id, sealTimeDraft, sealNumberDraft, exitEdit, handleTmsWhsSealedScanEventMutation]);

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="mt-1 space-y-3">
                {renderTimeRow(sealTimeLabel, sealTime, sealTimeTimezone, sealTimeDraft, setSealTimeDraft, true, waypoint.sealTimeTimezone)}
                {renderInputRow(sealNumberLabel, sealNumber, sealNumberDraft, setSealNumberDraft, t('tms.task.bol.placeholderSealNumber'), 32, 'max-w-[240px]', true)}
            </div>
        </div>
    );
};

export default SealingDetail;
